import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { employeesApi } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";

export const EmployeesTab = () => {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => { employeesApi.list({ q: q || undefined }).then(setRows).catch(() => toast.error(t("error"))); }, [q, t]);
  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  const remove = async () => {
    try { await employeesApi.remove(deleting.id); toast.success(t("employee_deleted")); load(); }
    catch (e) { toast.error(e?.response?.status === 400 ? t("employee_has_payslips") : t("error")); }
    finally { setDeleting(null); }
  };

  return (
    <div className="space-y-4" data-testid="employees-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="employees-search-input" />
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-800 hover:bg-blue-700" data-testid="create-employee-button">
          <Plus size={16} className="mr-1" /> {t("new_employee")}
        </Button>
      </div>
      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("employee")}</th>
                <th className="px-4 py-3">{t("position")}</th>
                <th className="px-4 py-3">{t("employment_type")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("vat_id")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("hire_date")}</th>
                <th className="px-4 py-3 text-right">{t("gross_salary")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`employee-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{e.last_name} {e.first_name}</div>
                    <div className="text-xs text-slate-500">{e.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{e.position || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-flex px-2 py-0.5 rounded border text-xs font-medium", e.employment_type === "part_time"
                      ? "text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800"
                      : "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800")}>
                      {t(e.employment_type)} · {e.weekly_hours}h
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell font-mono">{e.afm || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell font-mono text-slate-500">{fmtDate(e.hire_date, lang)}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium">{fmtMoney(e.gross_salary)}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider", e.active ? "text-emerald-700 dark:text-emerald-300" : "text-slate-400")}>
                      <span className={cn("w-1.5 h-1.5 rounded-full", e.active ? "bg-emerald-500" : "bg-slate-400")} />{e.active ? t("active") : t("inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditing(e); setOpen(true); }} data-testid={`employee-edit-${e.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Pencil size={15} /></button>
                      <button onClick={() => setDeleting(e)} data-testid={`employee-delete-${e.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-testid="employees-empty">{t("no_results")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <EmployeeDialog open={open} onOpenChange={setOpen} employee={editing} onSaved={load} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_employee")} onConfirm={remove} />
    </div>
  );
};
